import React from 'react';
import { BlogPost } from '../types';
import { ArrowRight, Calendar, User } from 'lucide-react';

interface BlogListProps {
  posts: BlogPost[];
  onReadPost: (post: BlogPost) => void;
}

const BlogList: React.FC<BlogListProps> = ({ posts, onReadPost }) => {
  return (
    <div className="bg-gray-50 min-h-screen pb-24">
        {/* Blog Hero */}
        <section className="bg-black py-24 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-orange-600/20 rounded-full blur-[100px] -mr-20 -mt-20 pointer-events-none"></div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
                <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-gray-900 border border-gray-800 mb-6">
                    <span className="text-orange-500 text-xs font-bold uppercase tracking-widest">
                        The BuildMySupplement Blog
                    </span>
                </div>
                <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 tracking-tight">
                    Supplement Manufacturing <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-orange-700">Insights</span>
                </h1>
                <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                    Expert advice on formulation, FSSAI compliance, and scaling your supplement business in India.
                </p>
            </div>
        </section>

        {/* Blog Grid */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative z-20">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {posts.map((post) => (
                    <article 
                        key={post.id} 
                        className="bg-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group cursor-pointer flex flex-col h-full"
                        onClick={() => onReadPost(post)}
                    >
                        {/* Image Container */}
                        <div className="relative h-48 overflow-hidden">
                            <img 
                                src={post.imageUrl} 
                                alt={post.title} 
                                loading="lazy"
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            />
                            <div className="absolute top-4 left-4 bg-orange-500 text-black text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                                {post.category}
                            </div>
                        </div>

                        {/* Content */}
                        <div className="p-8 flex-1 flex flex-col">
                            <div className="flex items-center text-xs text-gray-500 mb-4 space-x-4">
                                <div className="flex items-center">
                                    <Calendar className="w-3 h-3 mr-1" />
                                    {post.date}
                                </div>
                                <div className="flex items-center">
                                    <User className="w-3 h-3 mr-1" />
                                    {post.author}
                                </div>
                            </div>
                            
                            <h2 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-orange-600 transition-colors line-clamp-2">
                                {post.title}
                            </h2>
                            
                            <p className="text-gray-600 text-sm leading-relaxed mb-6 line-clamp-3">
                                {post.excerpt}
                            </p>
                            
                            <div className="mt-auto flex items-center text-orange-600 font-bold text-sm group-hover:translate-x-2 transition-transform">
                                Read Article <ArrowRight className="w-4 h-4 ml-2" />
                            </div>
                        </div>
                    </article>
                ))}
            </div>
        </section>
        
        {/* Newsletter / CTA */}
        <section className="max-w-4xl mx-auto px-4 mt-24 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Don't miss an update</h3>
            <p className="text-gray-600 mb-8">Join 2,000+ founders getting weekly manufacturing insights.</p>
            <form className="flex flex-col sm:flex-row justify-center gap-3 max-w-md mx-auto" onSubmit={(e) => e.preventDefault()}>
                <input 
                    type="email" 
                    placeholder="Enter your email" 
                    className="px-5 py-3 rounded-lg border border-gray-300 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                />
                <button className="px-6 py-3 bg-black text-white font-bold rounded-lg hover:bg-gray-800 transition-colors">
                    Subscribe
                </button>
            </form>
        </section>
    </div>
  );
};

export default BlogList;